import type { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { useUserPreferencesModal } from "../hooks/use_user_preferences_modal";
import { UserPreferencesTimezoneSelect } from "./user_preferences_timezone_select";
import { SeshSelect } from "../base_components/sesh_select_input";
import { UserPreferencesTimezoneItem } from "./items/user_preferences_timezone_item";
import { UserPreferencesBooleanItem } from "./items/use_preferences_boolean_item";
import { UserPreferencesAddonItem } from "./items/user_preferences_parse_item";

export type UserPreferenceModifiableSettings =
	keyof RouterInferenceHelpers["user"]["getPreferences"]["output"];

type UserPreferenceModifiableSettingsDescriptor = {
	label: string;
	description: string;
	Element: (props: {
		setting_key: UserPreferenceModifiableSettings;
		label: string;
		description: string;
		value: unknown;
	}) => JSX.Element;
};

const ModifiableSettingDescriptors: Record<
	UserPreferenceModifiableSettings,
	UserPreferenceModifiableSettingsDescriptor
> = {
	allow_event_rsvp_confirmation_dms: {
		label: "Event RSVP Confirmation DMs",
		description:
			"Determines whether to send confirmation DMs when you RSVP to an event. These DMs are necessary for receiving event reminders.",
		Element: UserPreferencesBooleanItem,
	},
	allow_event_rsvp_confirmation_replies: {
		label: "Event RSVP Confirmation Replies",
		description:
			"Determines whether to send ephemeral message replies when you RSVP to an event.",
		Element: UserPreferencesBooleanItem,
	},
	allow_poll_vote_confirmation_replies: {
		label: "Poll Vote Confirmation Replies",
		description:
			"Determines whether to send ephemeral message replies when you vote on a poll.",
		Element: UserPreferencesBooleanItem,
	},
	timezone: {
		label: "Personal Timezone",
		description:
			"The timezone sesh will use to display certain times, along with the timezone sesh will use to parse times created with slash commands if Parsing Timezone is set to Personal.",
		Element: UserPreferencesTimezoneItem,
	},
	should_parse_times_using_local_timezone: {
		label: "Parsing Timezone",
		description:
			"Determines what timezone sesh will use to parse event times when creating events with /create. The server timezone can be set on your server's settings page.",
		Element: UserPreferencesAddonItem,
	},
	send_rsvp_overlap_warning: {
		label: "Event RSVP Overlap Warning",
		description:
			"Determines whether to send a DM when you RSVP to an event that overlaps with another event you're attending.",
		Element: UserPreferencesBooleanItem,
	},
};

export function UserPreferencesModal() {
	const { is_open, setIsOpen } = useUserPreferencesModal();

	return (
		<>
			{is_open && (
				<DashboardUtils.SeshModal
					is_active={true}
					onClose={() => setIsOpen(false)}
					title="User Preferences"
					content={<UserPreferencesModalContent />}
				/>
			)}
		</>
	);
}

function UserPreferencesModalContent() {
	const preferences_query = trpc.user.getPreferences.useQuery();

	if (!preferences_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return (
		<div className="flex h-[512px] flex-col gap-4 overflow-auto py-2">
			{Object.keys(preferences_query.data).map(
				(setting: UserPreferenceModifiableSettings) => {
					const descriptor = ModifiableSettingDescriptors[setting];

					const value = preferences_query.data[setting];

					return (
						<descriptor.Element
							setting_key={setting}
							key={setting}
							description={descriptor.description}
							label={descriptor.label}
							value={value}
						/>
					);
				}
			)}
		</div>
	);
}

export function useUserPreferencesMutation(
	settings_key: UserPreferenceModifiableSettings
) {
	const context = trpc.useContext();

	return trpc.user.setPreferences.useMutation({
		onMutate: (t) => {
			context.user.getPreferences.setData((prev) => ({
				...prev,
				[settings_key]: t[settings_key],
			}));
		},
	});
}
